/**
 * Workspace layout — one ordering, one pinned set, shared by the visit view's
 * tab tray and the Free Compliance rail in the EMR tab.
 *
 * Everything the clinician can open for a visit is an item: the built-ins
 * (Note, Chat, Compliance, Coding) and every connected vendor app
 * (`app:<id>`). A pinned item gets its own tab and its own rail button; an
 * unpinned one lives in the Apps tab. The Apps entry itself is not an item —
 * it is always last, and only there when something is unpinned.
 *
 * Stored shape (LAYOUT_KEY): { order: [id], pinned: [id] }. `order` keeps ids
 * that are not available right now (an app that has not loaded yet, Compliance
 * on an account without it) so they come back where they were.
 *
 * Storage: the Comprehend account is the source of truth (/v1/user/settings
 * `workspace_layout`, read back from /v1/user/stats), so the layout follows
 * the clinician to any browser or machine. This browser keeps a cache that
 * the rail's content script reads and writes: chrome.storage.local in the
 * extension, localStorage on the web. The cache carries `owner` (whose layout
 * it is) and `dirty` (changed here, not yet on the account — the rail can't
 * call the API, so the side panel pushes its edits). See connectAccount().
 * The rail keeps its own copy of the keys and defaults (ComplianceRail.ts)
 * because the content script cannot import the React app.
 */
import { Emitter } from '../../agent/events';

export const LAYOUT_KEY = 'comprehendpt_workspace_layout';
/** chrome.storage.local: what this account can open, so the rail can draw app buttons. */
export const CATALOG_KEY = 'comprehendpt_workspace_catalog';
/** chrome.storage.local: the item the side panel has on screen, so the rail can mark its button. */
export const ACTIVE_KEY = 'comprehendpt_workspace_active';

export const APPS_ID = 'apps';
/** Items that can't be unpinned. None today: even the Note can live in Apps
 *  (it opens there, and everything that jumps to the Note follows it). Kept
 *  as a hook rather than removed, since the rail mirrors it. */
export const LOCKED = [];
export const DEFAULT_ORDER = ['note', 'chat', 'compliance', 'coding'];
// Today's tabs, in today's order: Coding only appears for accounts that have it.
export const DEFAULT_PINNED = ['note', 'chat', 'compliance', 'coding'];

/** The note's tab has always been keyed 'response' in the details views. */
export const tabKeyFor = (id) => (id === 'note' ? 'response' : id);
export const idForTabKey = (key) => (key === 'response' ? 'note' : key);
export const vendorItemId = (appId) => `app:${appId}`;
export const isVendorItem = (id) => typeof id === 'string' && id.startsWith('app:');
export const vendorAppIdOf = (id) => (isVendorItem(id) ? id.slice(4) : null);

const uniq = (list) => Array.from(new Set(list));
const cleanList = (value) => (Array.isArray(value) ? uniq(value.filter((v) => typeof v === 'string' && v)) : null);

export function normalizeLayout(stored) {
  const order = cleanList(stored?.order);
  const pinned = cleanList(stored?.pinned);
  return { order: order || [...DEFAULT_ORDER], pinned: pinned || [...DEFAULT_PINNED] };
}

/** Where an id nobody has placed yet goes: built-ins by their default slot, apps at the end. */
function insertUnseen(order, id) {
  const slot = DEFAULT_ORDER.indexOf(id);
  if (slot === -1) return [...order, id];
  // Before the first id that comes after it by default, else at the end.
  const later = DEFAULT_ORDER.slice(slot + 1);
  const at = order.findIndex((existing) => later.includes(existing));
  return at === -1 ? [...order, id] : [...order.slice(0, at), id, ...order.slice(at)];
}

/**
 * The layout as it applies to what is available now.
 * @param stored     whatever was in storage (or null)
 * @param available  ids that can be opened for this visit
 * @returns {{ layout, order, pinned: string[], unpinned: string[] }} `layout`
 *          is the stored layout with new ids placed (base every change on it);
 *          `pinned` and `unpinned` are only the available ids, in order.
 */
export function resolveLayout(stored, available) {
  const base = normalizeLayout(stored);
  let order = base.order;
  const pinnedSet = new Set([...base.pinned, ...LOCKED]);
  available.forEach((id) => {
    if (order.includes(id)) return;
    order = insertUnseen(order, id);
    // First sighting: pinned when it is pinned by default. After that the
    // stored pinned list is the clinician's answer.
    if (DEFAULT_PINNED.includes(id)) pinnedSet.add(id);
  });
  const here = order.filter((id) => available.includes(id));
  return {
    // What a change should start from, so first-sighting placements stick.
    layout: { order, pinned: order.filter((id) => pinnedSet.has(id)) },
    order,
    pinned: here.filter((id) => pinnedSet.has(id)),
    unpinned: here.filter((id) => !pinnedSet.has(id)),
  };
}

/** Move `id` to sit immediately before `beforeId` (null = to the end). */
export function moveItem(stored, id, beforeId) {
  const { order, pinned } = normalizeLayout(stored);
  if (id === beforeId) return { order, pinned };
  const without = order.filter((existing) => existing !== id);
  const at = beforeId == null ? -1 : without.indexOf(beforeId);
  const next = at === -1 ? [...without, id] : [...without.slice(0, at), id, ...without.slice(at)];
  return { order: next, pinned };
}

/**
 * Move `id` one step among `visible` (the ids the clinician is looking at, in
 * order) — the keyboard/button alternative to dragging.
 */
export function nudgeItem(stored, id, delta, visible) {
  const at = visible.indexOf(id);
  const to = at + delta;
  if (at === -1 || to < 0 || to >= visible.length) return normalizeLayout(stored);
  // Moving down past the next one = sit before the one after it.
  const beforeId = delta < 0 ? visible[to] : visible[to + 1] ?? null;
  return moveItem(stored, id, beforeId);
}

export function setPinned(stored, id, pinned) {
  const base = normalizeLayout(stored);
  if (LOCKED.includes(id)) return base;
  const order = base.order.includes(id) ? base.order : insertUnseen(base.order, id);
  const set = new Set(base.pinned);
  if (pinned) set.add(id);
  else set.delete(id);
  return { order, pinned: order.filter((existing) => set.has(existing)) };
}

/* -------------------------------------------------------------------------- */
/* Storage                                                                    */
/* -------------------------------------------------------------------------- */

const extensionArea = (area) => {
  try {
    // eslint-disable-next-line no-undef
    return typeof chrome !== 'undefined' && chrome.runtime?.id && chrome.storage?.[area] ? chrome.storage[area] : null;
  } catch (e) {
    return null;
  }
};

const local = new Emitter();
let cached = null;

const sameLayout = (a, b) => JSON.stringify(normalizeLayout(a)) === JSON.stringify(normalizeLayout(b));

function readArea(area) {
  return new Promise((resolve) => {
    try {
      area.get(LAYOUT_KEY, (result) => resolve(result?.[LAYOUT_KEY] || null));
    } catch (e) {
      resolve(null);
    }
  });
}

function writeCache(value) {
  embedRail.mirror?.('layout', value);
  const area = extensionArea('local');
  if (area) {
    try {
      area.set({ [LAYOUT_KEY]: value });
    } catch (e) {
      /* the in-memory layout still applies for this session */
    }
    return;
  }
  try {
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(value));
  } catch (e) {
    /* private mode: this session only */
  }
}

export async function loadLayout() {
  const area = extensionArea('local');
  if (area) {
    cached = await readArea(area);
    return cached;
  }
  try {
    cached = JSON.parse(localStorage.getItem(LAYOUT_KEY)) || null;
  } catch (e) {
    cached = null;
  }
  return Promise.resolve(cached);
}

export function saveLayout(layout) {
  const next = normalizeLayout(layout);
  if (cached && sameLayout(cached, next)) return;
  cached = { ...next, ...(account.owner ? { owner: account.owner } : {}), dirty: true };
  local.emit('layout', next);
  writeCache(cached);
  pushSoon();
}

/* -------------------------------------------------------------------------- */
/* The account                                                                */
/* -------------------------------------------------------------------------- */

const PUSH_DELAY_MS = 600;
// owner: the signed-in email; push: send a layout to the account (null until
// the backend is known to store one).
const account = { owner: null, push: null, timer: null };

function pushSoon() {
  if (!account.push) return;
  clearTimeout(account.timer);
  account.timer = setTimeout(async () => {
    const sent = cached;
    if (!sent?.dirty) return;
    try {
      const response = await account.push(normalizeLayout(sent));
      if (response?.error) return;
      // Only mark clean if nothing changed while it was in flight.
      if (cached === sent) {
        cached = { ...normalizeLayout(sent), owner: account.owner };
        writeCache(cached);
      }
    } catch (e) {
      /* stays dirty; the next change or visit tries again */
    }
  }, PUSH_DELAY_MS);
}

/**
 * Make the signed-in account the layout's home. Reads the account's layout
 * (stats.workspaceLayout) and reconciles it with this browser's cache:
 * - unsynced edits made here by this account win, and are pushed; so does an
 *   unowned one (a rail edit before the panel first connected here), if the
 *   account has none yet;
 * - otherwise the account's layout replaces the cache (and reaches the rail);
 * - a cache that belongs to someone else is dropped.
 * A backend that predates the setting (no workspaceLayout key at all) leaves
 * everything local, as before.
 */
export async function connectAccount(apiClient) {
  if (!apiClient?.getStats) return;
  let stats;
  try {
    stats = await apiClient.getStats();
  } catch (e) {
    return;
  }
  if (!stats || stats.error || !('workspaceLayout' in stats)) return;
  const owner = stats.email || apiClient.email || null;
  account.owner = owner;
  account.push = (layout) => apiClient.editUserSettings({ workspace_layout: layout });

  if (cached === null) await loadLayout();
  const remote = stats.workspaceLayout ? normalizeLayout(stats.workspaceLayout) : null;
  // Our own unsynced edit always wins. An unowned one (the rail, before the
  // panel connected in this browser) only fills an account that has none yet.
  const pending = cached?.dirty && (cached.owner === owner || (!cached.owner && !remote));

  if (pending && !(remote && sameLayout(cached, remote))) {
    cached = { ...cached, owner };
    pushSoon();
    return;
  }
  const next = remote ? { ...remote, owner } : null;
  const changed = !(cached && next && sameLayout(cached, next)) && !(cached === null && next === null);
  cached = next;
  writeCache(next);
  if (changed) local.emit('layout', next);
}

/** Layout changes from anywhere: this page, another tab, or the rail. */
export function subscribeLayout(cb) {
  const offLocal = local.on('layout', cb);
  const offs = [offLocal];
  try {
    // eslint-disable-next-line no-undef
    const onChanged = typeof chrome !== 'undefined' && chrome.storage?.onChanged;
    if (onChanged && extensionArea('local')) {
      const listener = (changes, area) => {
        if (area !== 'local' || !changes[LAYOUT_KEY]) return;
        const value = changes[LAYOUT_KEY].newValue || null;
        if (cached && value && sameLayout(cached, value)) return;
        cached = value;
        cb(value);
        // A rearrange on the rail: it can't reach the account, so we do.
        if (value?.dirty) pushSoon();
      };
      onChanged.addListener(listener);
      offs.push(() => onChanged.removeListener(listener));
    }
  } catch (e) {
    /* no extension runtime */
  }
  if (typeof window !== 'undefined') {
    const onStorage = (e) => {
      if (e.key !== LAYOUT_KEY) return;
      try {
        cached = JSON.parse(e.newValue) || null;
      } catch (err) {
        cached = null;
      }
      cb(cached);
    };
    window.addEventListener('storage', onStorage);
    offs.push(() => window.removeEventListener('storage', onStorage));
  }
  return () => offs.forEach((off) => off());
}

/* -------------------------------------------------------------------------- */
/* Catalog for the rail                                                       */
/* -------------------------------------------------------------------------- */

const ICON_PX = 40; // painted at 20 CSS px on the rail, so crisp at 2x
const pixelCache = new Map(); // icon URL → Promise<{ size, data } | null>

/**
 * An app's icon as raw RGBA pixels (base64). The rail runs inside the
 * customer's EMR page, where loading the vendor's URL would be a request from
 * their EMR and their CSP may refuse images outright — but it can always paint
 * pixels onto a canvas. Fetched here, from the extension page (whose host
 * permissions make this CORS-free), and decoded through a blob URL so the
 * canvas is never tainted; SVG icons work too.
 */
export function rasterizeIcon(url) {
  if (!url || typeof document === 'undefined' || typeof fetch !== 'function') return Promise.resolve(null);
  if (!pixelCache.has(url)) {
    pixelCache.set(
      url,
      (async () => {
        try {
          const res = await fetch(url, { credentials: 'omit' });
          if (!res.ok) return null;
          const objectUrl = URL.createObjectURL(await res.blob());
          try {
            const img = await new Promise((resolve, reject) => {
              const image = new Image();
              image.onload = () => resolve(image);
              image.onerror = reject;
              image.src = objectUrl;
            });
            const canvas = document.createElement('canvas');
            canvas.width = ICON_PX;
            canvas.height = ICON_PX;
            const ctx = canvas.getContext('2d');
            // Cover, like the launcher tile's object-fit. An SVG with no size of its own reports 0.
            const w = img.naturalWidth || ICON_PX;
            const h = img.naturalHeight || ICON_PX;
            const scale = Math.max(ICON_PX / w, ICON_PX / h);
            ctx.drawImage(img, (ICON_PX - w * scale) / 2, (ICON_PX - h * scale) / 2, w * scale, h * scale);
            const { data } = ctx.getImageData(0, 0, ICON_PX, ICON_PX);
            let binary = '';
            for (let i = 0; i < data.length; i += 0x8000) binary += String.fromCharCode.apply(null, data.subarray(i, i + 0x8000));
            return { size: ICON_PX, data: btoa(binary) };
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        } catch (e) {
          return null;
        }
      })(),
    );
  }
  return pixelCache.get(url);
}

let publishGeneration = 0;

/**
 * Tell the rail what exists: [{ id, label, color?, iconPixels? }]. Extension
 * only — the web app has no rail. Written to local (not sync): it is this
 * browser's view of the account, and icons would eat the sync quota.
 */
export async function publishCatalog(items) {
  const area = extensionArea('local');
  if (!area && !embedRail.mirror) return;
  const generation = ++publishGeneration;
  const catalog = await Promise.all(
    items.map(async ({ id, label, icon, color, attention }) => {
      const iconPixels = icon ? await rasterizeIcon(icon) : null;
      // `attention`: not linked to the open patient yet. A flag only: no
      // patient details go into extension storage.
      return { id, label, ...(color ? { color } : {}), ...(iconPixels ? { iconPixels } : {}), ...(attention ? { attention: true } : {}) };
    }),
  );
  if (generation !== publishGeneration) return; // a newer list is on its way
  const value = { items: catalog, updatedAt: Date.now() };
  embedRail.catalog = value;
  embedRail.mirror?.('catalog', value);
  try {
    area?.set({ [CATALOG_KEY]: value });
  } catch (e) {
    /* the rail falls back to its built-in buttons */
  }
}

/**
 * Tell the rail which item is on screen ('apps' when the Apps tab is, or null
 * when the panel goes away). Extension only.
 */
export function publishActive(id) {
  const value = id ? { id, at: Date.now() } : null;
  embedRail.active = value;
  embedRail.mirror?.('active', value);
  try {
    extensionArea('local')?.set({ [ACTIVE_KEY]: value });
  } catch (e) {
    /* the rail just won't mark a button */
  }
}

/* -------------------------------------------------------------------------- */
/* The embed's rail                                                           */
/*                                                                            */
/* In the embed (cx_embed loader.js) the rail lives on the EMR page with no    */
/* extension storage, and this panel is an iframe. The panel's bridge         */
/* (react_webInjector.ts setupComplianceRailBridge) relays: it answers the    */
/* rail's WORKSPACE_STATE / WORKSPACE_LAYOUT_SET, and every layout, catalog    */
/* and active change here is pushed to the rail through the mirror.           */
/* -------------------------------------------------------------------------- */

const embedRail = { mirror: null, catalog: null, active: null };

/** Send every change of 'layout' | 'catalog' | 'active' to the embed's rail. Returns an unsubscribe. */
export function setRailMirror(fn) {
  embedRail.mirror = fn;
  return () => {
    if (embedRail.mirror === fn) embedRail.mirror = null;
  };
}

/** What the rail needs to draw itself: the cached layout, the app catalog, the open item. */
export async function railState() {
  const layout = cached === null ? await loadLayout() : cached;
  return { layout, catalog: embedRail.catalog, active: embedRail.active };
}

/** A rearrangement made on the embed's rail: saved here (and to the account) like one made on the tabs. */
export function saveLayoutFromRail(layout) {
  saveLayout(layout);
}

/** For tests. */
export function __resetLayoutCache() {
  cached = null;
  embedRail.mirror = null;
  embedRail.catalog = null;
  embedRail.active = null;
  clearTimeout(account.timer);
  account.owner = null;
  account.push = null;
}
